import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { LoadingSpinner } from '../LoadingSpinner';

const showLoadingScreenDelay = 200;
const defaultLoading = <LoadingSpinner />;

export function lazyLoad({ fallback, loader, loading = defaultLoading }) {
    function LazyLoadWrapper(props) {
        const [showLoadingScreen, setShowLoadingScreen] = useState(false);
        const [isLoading, setIsLoading] = useState(true);
        const retry = useCallback(() => setIsLoading(true), []);

        useEffect(() => {
            const timeout = setTimeout(() => setShowLoadingScreen(true), showLoadingScreenDelay);
            return () => clearTimeout(timeout);
        }, []);

        const LazyFallbackComponent = useMemo(
            () => Promise.resolve({ default: () => fallback({ retry }) }),
            [fallback, retry],
        );

        const LoadingComponent = useMemo(
            () => (showLoadingScreen ? loading : null),
            [loading, showLoadingScreen],
        );

        const LazyComponent = useMemo(
            () =>
                isLoading
                    ? lazy(() =>
                          loader().catch(() => {
                              setIsLoading(false);
                              return LazyFallbackComponent;
                          }),
                      )
                    : lazy(() => LazyFallbackComponent),
            [loader, isLoading, LazyFallbackComponent],
        );

        return (
            <Suspense fallback={LoadingComponent}>
                <LazyComponent {...props} />
            </Suspense>
        );
    }

    return LazyLoadWrapper;
}
